/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { z } from 'zod';

/**
 * Shapes read off the running API, not invented here.
 *
 * These are zod schemas rather than bare TypeScript types on purpose: they check
 * at **runtime**, at the boundary. If the server's shape drifts, the client fails
 * loudly at the exact point of drift instead of producing `undefined` three
 * components later. Shared compile-time types could not have caught that — they
 * would only ever have agreed with themselves. It is also why the server stays
 * plain JavaScript (plan §12.5).
 */

/** GET /auth/me — everything the client is allowed to know about who it is. */
export const SessionUser = z.object({
  email: z.string(),
  name: z.string().nullable().optional(),
});
export type SessionUser = z.infer<typeof SessionUser>;

/**
 * One column of one circle's board.
 *
 * `color` is a **palette token name** — 'action', 'teal' — never a hex. The
 * board turns it into `var(--color-<token>)`, which is what keeps the columns,
 * the modal's chips and the calendar's tints from ever disagreeing about what a
 * column looks like, and what stops an unreadable colour reaching a badge.
 * Empty means "the default for this slot", which is how the five original
 * columns need no stored colour at all.
 */
export const Vertical = z.object({
  id: z.string(),
  label: z.string(),
  color: z.string().default(''),
  position: z.number(),
  /** Only sent by the admin endpoint, where the delete confirmation needs it. */
  taskCount: z.number().optional(),
});
export type Vertical = z.infer<typeof Vertical>;

export const Circle = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
  memberCount: z.number(),
  // Whether *you* administer *this* circle — distinct from `isAdmin` below,
  // which only says you administer something. Optional so an older server
  // response still parses; absent is read as "no".
  canAdminister: z.boolean().optional(),
  /**
   * Whether you *belong to* this circle, as opposed to merely being able to
   * open it. The two differ only for a super admin, who can reach every circle
   * and works in two or three — which is exactly the case the switcher folds
   * away. Optional, and **absent is read as true**: an older server does not
   * send it, and for everyone but a super admin every reachable circle is one
   * they are in, so defaulting to false would empty the menu.
   */
  isMember: z.boolean().optional(),
  /**
   * **This circle's columns, and the reason they travel with the session.**
   *
   * The board cannot draw without them; fetching them separately would paint
   * five default columns for a beat and then rearrange. Optional so an older
   * server response still parses — `verticalsOf()` in queries/session.ts falls
   * back to the five defaults, which is also what an un-customised circle
   * means.
   */
  verticals: z.array(Vertical).optional(),
});
export type Circle = z.infer<typeof Circle>;

/**
 * GET /api/circles.
 *
 * `activeId` and `mode` are **server state**. The active circle is persisted per
 * person through POST /api/circles/active and must not become a client concern or
 * a URL parameter — a link carrying it would silently switch the circle of
 * whoever opened it.
 *
 * `canSwitch` is false for somebody in a single circle, and the switcher is then
 * not rendered at all rather than shown disabled: there is nothing to switch
 * between, and a disabled control only invites a click.
 *
 * `isAdmin` and `isSuperAdmin` are two separate answers, not a rank to derive.
 * An ordinary admin sees only the circles they belong to or created; super admins
 * (from ADMIN_EMAILS) keep the wide view and can never be revoked.
 */
export const CirclesResponse = z.object({
  circles: z.array(Circle),
  activeId: z.string(),
  mode: z.string(),
  canSwitch: z.boolean(),
  isAdmin: z.boolean(),
  isSuperAdmin: z.boolean(),
});
export type CirclesResponse = z.infer<typeof CirclesResponse>;
